/**
 * Very small Markdown-to-JSX renderer for AI output.
 * Supports: # headings, **bold**, `code`, - bullets, tables (basic), paragraphs, line breaks.
 * Kept intentionally simple — no external dep.
 */
import React from "react";

function inline(text) {
  // bold, code
  const parts = [];
  let rest = text;
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith("**")) {
      parts.push(
        <strong key={key++} className="font-semibold text-slate-900">
          {token.slice(2, -2)}
        </strong>
      );
    } else if (token.startsWith("`")) {
      parts.push(
        <code key={key++} className="mono bg-slate-100 px-1 py-0.5 text-xs">
          {token.slice(1, -1)}
        </code>
      );
    }
    last = m.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export default function Markdown({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Headings
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const cls =
        level === 1
          ? "font-display font-black text-lg tracking-tight mt-3 mb-1"
          : level === 2
          ? "font-display font-bold text-base tracking-tight mt-3 mb-1"
          : "font-semibold text-sm mt-2 mb-1";
      blocks.push(
        <div key={key++} className={cls}>
          {inline(h[2])}
        </div>
      );
      i++;
      continue;
    }
    // Bullet list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i++;
      }
      blocks.push(
        <ul key={key++} className="list-disc pl-5 space-y-1 my-2 text-sm text-slate-700">
          {items.map((it, k) => (
            <li key={k}>{inline(it)}</li>
          ))}
        </ul>
      );
      continue;
    }
    // Table
    if (/^\|.*\|$/.test(line) && i + 1 < lines.length && /^\|[\s\-|:]+\|$/.test(lines[i + 1])) {
      const header = line.split("|").slice(1, -1).map((c) => c.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && /^\|.*\|$/.test(lines[i])) {
        rows.push(lines[i].split("|").slice(1, -1).map((c) => c.trim()));
        i++;
      }
      blocks.push(
        <div key={key++} className="my-2 overflow-x-auto">
          <table className="text-xs border border-slate-200 tabular">
            <thead className="bg-slate-50">
              <tr>
                {header.map((h, k) => (
                  <th key={k} className="px-2 py-1 text-left border-b border-slate-200 font-semibold">
                    {inline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, k) => (
                <tr key={k}>
                  {r.map((c, j) => (
                    <td key={j} className="px-2 py-1 border-b border-slate-100">
                      {inline(c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    // Blank
    if (line.trim() === "") {
      i++;
      continue;
    }
    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      blocks.push(<hr key={key++} className="my-3 border-slate-200" />);
      i++;
      continue;
    }
    // Paragraph
    blocks.push(
      <p key={key++} className="text-sm text-slate-700 leading-relaxed my-1.5">
        {inline(line)}
      </p>
    );
    i++;
  }
  return <div>{blocks}</div>;
}
